import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTable } from "@refinedev/antd";
import {
  Input,
  Table,
  theme,
  type TableColumnsType,
  Form,
  Grid,
  Flex,
  Typography,
  Tag,
  Button,
  Badge,
  Card,
  message,
} from "antd";
import { CrudFilters, HttpError, useGetIdentity } from "@refinedev/core";
import {
  ApiOutlined,
  CheckOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { PaginationTotal } from "@/components/pagination-total";
import { TFunction, i18n as I18nType } from "i18next";
import { useTranslation } from "react-i18next";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { User } from "@/types/schema/schema.types";
import { useNavigate } from "react-router-dom";
import {
  API_URL_PERMISSIONS_STORES,
  API_URL_STORE_LINKED_SELLER,
} from "@/services/XCONS_ENV";
import { KeyRound } from "@/components/icon/Key";
import { useSmartMenuLink } from "@/config/context/smartMenuNavigation";
import {
  FilterTags,
  LateralModal,
  NormalModal,
  SellerIcon,
} from "@/components";
import { LinkedStoreFilters } from "./linkedStoreFilters";
import { ExternalLink } from "@/components/icon/ExternalLink";
import { axiosInstance } from "@/utilities";
import dayjs from "dayjs";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

const { Title, Text, Link } = Typography;
const { Search } = Input;

export const LinkedStoreList: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const screens = Grid.useBreakpoint();
  const { token } = theme.useToken();
  const { data: dataLoguinUser } = useGetIdentity<User>();
  const { smartMenuName } = useContext(SmartMenuContext);
  const [isRequestLoading, setIsRequestLoading] = useState<number | null>(null);
  const [typeIdentifier, setTypeIdentifier] = useState("seller");
  const [localData, setLocalData] = useState<any[]>([]);

  const storeCode = encodeURIComponent(
    smartMenuName?.replace(/"/g, "").toLowerCase(),
  );

  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();

  const onSearch = (params: any) => {
    const filters: CrudFilters = [];
    const {
      main_search,
      storeStatus,
      linkStatus,
      requestStatus,
      filters: existingFilters,
    } = params;

    if (existingFilters) {
      existingFilters.forEach((filter: any) => {
        filters.push(filter);
      });
    }

    filters.push(
      {
        field: "search",
        operator: "contains",
        value: main_search,
      },
      {
        field: "storeLinkedSellers.active",
        operator: "eq",
        value: storeStatus,
      },
      {
        field: "storeLinkedSellerRequests.active",
        operator: "eq",
        value: linkStatus,
      },
      {
        field: "storeLinkedSellerRequests.active",
        operator: "eq",
        value: requestStatus,
      },
    );

    setFilters(filters);
    return filters;
  };

  const {
    tableQuery: { data, isLoading, isFetching, refetch },
    searchFormProps,
    tableProps,
    filters,
    setFilters,
  } = useTable<HttpError>({
    resource: `store-linked-seller/${storeCode}/stores`,
    pagination: {
      pageSize: FILAS_POR_PAGINA,
    },
    syncWithLocation: true,
    onSearch: onSearch,
  });

  const isLoadingTable = !data || isFetching;

  const total = data?.total || 0;

  const dataSource = useMemo(
    () => (isLoadingTable ? [] : localData || []),
    [isLoadingTable, localData],
  );

  useEffect(() => {
    if (data?.data) {
      setLocalData(data.data);
    }
  }, [data?.data]);

  const updateLocalStore = (storeId: string, updates: Partial<any>) => {
    setLocalData((prev) =>
      prev.map((store) =>
        store.storeId === storeId ? { ...store, ...updates } : store,
      ),
    );
  };

  const handleLinkRequest = async (record: any) => {
    setIsRequestLoading(record.storeId);

    // If the OPPOSITE party already sent a request, this click is an Accept:
    // the backend will auto-create the link instead of a new request, so
    // don't overwrite the existing request with a fake one. Otherwise this
    // is a fresh request from our side.
    const acceptingExisting =
      !!record.storeLinkedSellerRequests &&
      record.storeLinkedSellerRequests.requestBy === "store";
    const previousState = localData.find((s) => s.storeId === record.storeId);

    if (acceptingExisting) {
      updateLocalStore(record.storeId, {
        storeLinkedSellers: {
          storeSellerId: record.storeLinkedSellerRequests.id,
          active: true,
          createdAt: new Date().toISOString(),
        },
        // Drop the pending request — once linked it's no longer pending.
        storeLinkedSellerRequests: null,
      });
    } else {
      const tempId = `temp-${Date.now()}`;
      updateLocalStore(record.storeId, {
        storeLinkedSellerRequests: {
          id: tempId,
          storeCode: storeCode,
          sellerCode: record.sellerCode,
          createdAt: new Date().toISOString(),
          requestBy: typeIdentifier,
          active: true,
        },
      });
    }

    try {
      const payload = {
        storeCode: record.storeCode,
        sellerCode: record.sellerCode,
        requestBy: typeIdentifier,
        active: true,
      };

      const response = await axiosInstance.post(
        `${API_URL_STORE_LINKED_SELLER}/request`,
        payload,
      );

      // Same dual-shape response as the store side: the endpoint returns a
      // StoreLinkedSeller (link) when auto-resolving an opposite request,
      // and a StoreLinkedSellerRequest otherwise. Discriminator: presence
      // of `storeSellerId` (Link entity has it, Request entity doesn't).
      // We can't use absence-of-requestBy because TypeORM `save(dto)` mutates
      // and returns the input object, so the Link response carries a
      // "ghost" `requestBy` field copied from the DTO body.
      const data = response.data;
      const isLink =
        !!data &&
        typeof data === "object" &&
        ("storeSellerId" in data || "store_seller_id" in data);

      if (isLink) {
        updateLocalStore(record.storeId, {
          storeLinkedSellers: data,
          storeLinkedSellerRequests: null,
        });
      } else {
        updateLocalStore(record.storeId, {
          storeLinkedSellerRequests: data,
        });
      }

      message.success("Request sent successfully");
    } catch (error: any) {
      if (previousState) {
        updateLocalStore(record.storeId, {
          storeLinkedSellers: previousState.storeLinkedSellers,
          storeLinkedSellerRequests: previousState.storeLinkedSellerRequests,
        });
      } else {
        updateLocalStore(record.storeId, {
          storeLinkedSellerRequests: null,
        });
      }
      console.error("Error sending request:", error?.message);
      message.error(error?.message || "Failed to send request");
    } finally {
      setIsRequestLoading(null);
    }
  };

  const handleCancelRequest = async (record: any) => {
    setIsRequestLoading(record.storeId);
    console.log("record", record);

    const previousState = localData.find((s) => s.storeId === record.storeId);
    updateLocalStore(record.storeId, {
      storeLinkedSellers: null,
      storeLinkedSellerRequests: null,
    });

    try {
      await axiosInstance.delete(
        `${API_URL_STORE_LINKED_SELLER}/request/${record?.storeLinkedSellerRequests?.id}`,
      );

      message.success("Request cancelled successfully");
    } catch (error: any) {
      if (previousState) {
        updateLocalStore(record.storeId, {
          storeLinkedSellers: previousState.storeLinkedSellers,
          storeLinkedSellerRequests: previousState.storeLinkedSellerRequests,
        });
      }

      console.error("Error cancelling request:", error?.message);
      message.error(error?.message || "Failed to cancel request");
    } finally {
      setIsRequestLoading(null);
    }
  };

  const columns: TableColumnsType<any> = [
    {
      key: "name",
      title: "Stores",
      sorter: (a, b) => a.name.localeCompare(b.name),
      dataIndex: "name",
      render: (_, record) => {
        return (
          <Flex align="center" gap={4}>
            <ShopOutlined />
            <Text>{record.name}</Text>
            <Link
              href={`https://www.xcons.com.ar/storelocation/${record?.storeCode}`}
              target="_blank"
            >
              <ExternalLink />
            </Link>
          </Flex>
        );
      },
    },
    {
      key: "sellerCode",
      title: t("operator.linkStatus"),
      dataIndex: "sellerCode",
      render: (_, record) => {
        const linkedSeller = Array.isArray(record.storeLinkedSellers)
          ? record.storeLinkedSellers[0]
          : record.storeLinkedSellers;
        const hasLink = !!linkedSeller;

        return (
          <Flex align="start" gap={4} vertical>
            {hasLink && (
              <>
                <Flex gap={4}>
                  <Badge
                    status={linkedSeller?.active ? "success" : "warning"}
                  />
                  <Text>
                    Store tiene el Seller{" "}
                    {linkedSeller?.active
                      ? "Activo"
                      : "Inactivo"}
                  </Text>
                </Flex>
                <Flex gap={4}>
                  <Badge
                    status={record?.isActive ? "success" : "warning"}
                  />
                  <Text>
                    Seller con ventas{" "}
                    {record?.isActive ? "Activo" : "Inactivo"}
                  </Text>
                </Flex>
              </>
            )}
          </Flex>
        );
      },
      width: "50%",
    },
    {
      key: "sellerId",
      title: t("linkedsellers.table.request"),
      dataIndex: "sellerId",
      render: (_, record) => {
        const linkedSeller = Array.isArray(record.storeLinkedSellers)
          ? record.storeLinkedSellers[0]
          : record.storeLinkedSellers;

        const request = Array.isArray(record.storeLinkedSellerRequests)
          ? record.storeLinkedSellerRequests[0]
          : record.storeLinkedSellerRequests;

        const hasLink = !!linkedSeller;
        const hasRequest = !!request;

        const shouldShowLinkRequest = !hasLink && !hasRequest;
        const isSellerRequest = request?.requestBy === "seller";
        const isStoreRequest = request?.requestBy === "store";

        return (
          <Flex vertical justify="end" align="end">
            {shouldShowLinkRequest && (
              <>
                <Button
                  loading={isRequestLoading === record.storeId}
                  size="small"
                  icon={<ApiOutlined />}
                  iconPosition="end"
                  onClick={() => handleLinkRequest(record)}
                >
                  {t("button.table.linkedsellers.linkrequest")}
                </Button>
              </>
            )}

            {isSellerRequest && !hasLink && (
              <Flex gap={8} vertical align="end">
                <Text style={{ color: token.colorTextSecondary }}>
                  {t("table.linkedsellers.description.requestsenton")}{" "}
                  {dayjs(request?.createdAt).format("DD/MM/YYYY")}
                </Text>
                <Button
                  loading={isRequestLoading === record.storeId}
                  type="link"
                  danger
                  size="small"
                  onClick={() => handleCancelRequest(record)}
                >
                  {t("orders.tag.cancelrequest")}
                </Button>
              </Flex>
            )}

            {isStoreRequest && !hasLink && (
              <Flex gap={8} vertical align="end">
                <Text style={{ color: token.colorTextSecondary }}>
                  {t("table.linkedsellers.description.requestreceivedon")}{" "}
                  {dayjs(request?.createdAt).format("DD/MM/YYYY")}
                </Text>
                <Flex align="center" gap={8}>
                  <Button
                    loading={isRequestLoading === record.storeId}
                    type="link"
                    danger
                    size="small"
                    onClick={() => handleCancelRequest(record)}
                  >
                    {t("button.table.linkedsellers.reject")}
                  </Button>
                  <Button
                    loading={isRequestLoading === record.storeId}
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />} iconPosition="end"
                    onClick={() => handleLinkRequest(record)}
                  >
                    {t("button.table.linkedsellers.accept")}
                  </Button>
                </Flex>
              </Flex>
            )}

            {hasLink && (
              <Flex gap={8} vertical align="end">
                <Text style={{ color: token.colorTextSecondary }}>
                  {t("table.linkedsellers.description.linkedselleron")}{" "}
                  {dayjs(linkedSeller?.createdAt).format("DD/MM/YYYY")}
                </Text>
                <Button
                  loading={isRequestLoading === record.storeId}
                  size="small"
                  icon={<EyeOutlined />}
                  iconPosition="end"
                  onClick={() => handleRowClick(record)}
                >
                  Reglas del Store
                </Button>
              </Flex>
            )}
          </Flex>
        );
      },
    },
  ];

  const HeaderRow = React.memo(() => (
    <>
      <Flex flex="block" gap="large" wrap={screens.sm ? false : true}>
        <Form {...searchFormProps} style={{ width: "100%" }}>
          <Form.Item name="main_search">
            <Search
              placeholder={"Buscar por nombre"}
              allowClear
              onSearch={(value) => {
                if (value.trim() === "") {
                  searchFormProps.form?.setFieldsValue({
                    main_search: undefined,
                  });
                }
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
        </Form>

        <Flex
          gap="large"
          justify="center"
          style={{ width: screens.sm ? "auto" : "100%" }}
          className="add-user-button"
        >
          <LateralModal
            disabled={isFetching}
            buttonName={t("buttons.filters")}
            title={t("filter.title.addFilter")}
          >
            <LinkedStoreFilters
              searchFormProps={searchFormProps}
              filters={filters}
              t={t}
            />
          </LateralModal>
        </Flex>
      </Flex>

      <FilterTags
        searchFormProps={searchFormProps}
        onSearch={onSearch}
        i18n={i18n}
        filters={filters}
        setFilters={setFilters}
        t={t}
      />
    </>
  ));

  const navigate = useNavigate();

  const handleRowClick = (record: any) => {
    navigate(`/sellers/linked-stores/${storeCode}/${record?.storeCode}`, {
      state: { record: record },
    });
  };

  return (
    <>
      <Flex align="baseline" gap={8}>
        <SellerIcon style={{ transform: "scale(1.6)", height: 14 }} />
        <Title level={3} style={{ display: "flex", alignItems: "center" }}>
          <span style={{ textTransform: "capitalize", marginRight: 5 }}>
            {storeCode}
          </span>
          linked stores
        </Title>
      </Flex>
      <Table
        {...tableProps}
        scroll={{ x: true }}
        title={() => <HeaderRow />}
        rowKey={(record) => record?.sellerId}
        dataSource={dataSource}
        columns={columns}
        loading={isFetching}
        locale={{
          triggerAsc: t("table.triggerAsc"),
          triggerDesc: t("table.triggerDesc"),
          cancelSort: t("table.cancelSort"),
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: OPCIONES_POR_PAGINA,
          showQuickJumper: false,
          locale: {
            items_per_page: t("table.itemsPerPage"),
            prev_page: t("table.prevPage"),
            next_page: t("table.nextPage"),
            jump_to: t("table.jumpTo"),
            jump_to_confirm: t("table.jumpToConfirm"),
            page: t("table.page"),
            next_5: t("table.next5"),
            prev_5: t("table.prev5"),
          },
          showTotal: (total) => (
            <PaginationTotal total={total} entityName="stores" />
          ),
        }}
      />
    </>
  );
};
